import type { AxiosResponse } from 'axios'
import type { InsuranceParamsDto, OfferAddon } from 'BrokerTsLib/src/dtos'
import { Risk, RiskVariant } from 'BrokerTsLib/src/enums'
import { createContext, useContext, useEffect, useState } from 'react'
import type { UseMutateAsyncFunction } from '@tanstack/react-query'

import { useInsuranceParams } from './InsuranceParamsProvider'
import { useOffersAddonContext } from './OffersAddonProvider'
import type { GetOffersParams } from './OffersProvider'
import { useSelectedQuote } from './SelectedQuoteContext'

type AddonOffersCtxProps = {
  addonOffers: OfferAddon[]
  refetch: UseMutateAsyncFunction<AxiosResponse<OfferAddon[], any>, any, GetOffersParams, any>
  isLoadingData: boolean
  isFetched: boolean
}

const selectedOffersAddonContext = createContext<AddonOffersCtxProps>({} as AddonOffersCtxProps)

export const SelectedOffersAddonProvider = ({ children }: { children: React.ReactNode }) => {
  const { insuranceParams: ip } = useInsuranceParams()
  const insuranceParams = ip as InsuranceParamsDto
  const { offer } = useSelectedQuote()
  const [addonOffers, setAddonOffers] = useState<OfferAddon[]>([])
  const { refetchOffers: refetchAddonOffers, offers: allAddonOffers, isLoading, isFetchedOffers } = useOffersAddonContext()

  useEffect(() => {
    refetchAddonOffers({})
  }, [])

  useEffect(() => {
    if (allAddonOffers.length > 0) {

      let selectedAddonOffers: OfferAddon[] = []
      insuranceParams!.baseParams.addonVariants
        ?.filter(x => x.selected)
        .forEach(av => {
          // v pripade varianty allrisk + gap se hleda linkovana offera
          if (offer &&
                        av.riskVariantId == RiskVariant.AddonGAP &&
                        insuranceParams!.baseParams.variants?.find(x => x.id == offer?.insuranceVariantId)?.variantParts?.find(x => x.risk == Risk.HAV)?.riskVariant == RiskVariant.AllriskPlusGAP
          ) {
            const gapOffer = allAddonOffers.find(x => x.riskVariantId == RiskVariant.AddonGAP && (x.linkedOfferId?.includes(offer.offerId) ?? false))
            if (gapOffer)
              selectedAddonOffers.push(gapOffer)
          } else {
            selectedAddonOffers = [
              ...selectedAddonOffers,
              ...allAddonOffers.filter(x => x.riskVariantId == av.riskVariantId && x.isActive)
            ]
          }
        })

      setAddonOffers(selectedAddonOffers)
    }
  }, [offer?.offerId, allAddonOffers])

  return (
    <selectedOffersAddonContext.Provider
      value={{
        addonOffers: addonOffers,
        refetch: refetchAddonOffers,
        isLoadingData: isLoading,
        isFetched: isFetchedOffers
      }}>
      {children}
    </selectedOffersAddonContext.Provider>
  )
}

export const useSelectedOffersAddonContext = () => {
  const context = useContext(selectedOffersAddonContext)

  if (context === undefined || Object.keys(context).length == 0) {
    throw new Error('useSelectedOffersAddonContext must be used within a SelectedOffersAddonProvider!')
  }

  return context
}
